import { ItemRequest, MaterialBranch, Task, TaskDevices } from '../../models/dashboard-models';
import React, { Suspense, useContext, useEffect, useState } from 'react';
import { defaultDevice } from '../../utils/Enums/NewRequest';
import { CustomButton } from '../CustomButton';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import {
    BatteryPassport,
    BranchDevice,
    OilPassport,
    ShortDevicePassport,
    WorkHours,
} from '../../models/warehouse-models';
import { TasksContext } from '../../context/TasksContext';
import { Button, Form, Spinner } from 'react-bootstrap';
import { PassportImages } from '../../pages/passports/PassportDetails';
import noImage from '../../assets/No_image_available.png';
import { Control, Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import {
    BatteryPassportSelectStyles,
    devicePassportsSelectStyles,
    oilPassportSelectStyles,
} from '../../styles/selectStyles';
import { WorkHoursInner } from '../NewRequest/NewRequestDevice';
import {
    checkForItemsInInventory,
    useGetAllBatteriesPassports,
    useGetAllOilPassports,
    useGetAllShortDevicePassports,
} from '../../utils/warehouses-hooks';
import { updateTaskNeededItems } from '../../utils/dashboard-hooks';
import { NewRequestMaterialsTable } from '../Dashboard/NewRequestMaterialsTable';
import { StepWizardChildProps } from 'react-step-wizard';
import { useGetBranchPaymentInfo } from '../../utils/clients-hooks';

interface CustomStepWizardChildProps extends Partial<StepWizardChildProps> {
    task: Task;
    refetchTasks: () => void;
}

export const EditBranchDevicesStep: React.FC<CustomStepWizardChildProps> = ({
    nextStep,
    task,
    refetchTasks,
}: CustomStepWizardChildProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { refetchTask, setTaskDevices, setUsedItems, materials, setMaterials } = useContext(TasksContext);
    const [hasChanges, setHasChanges] = useState(false);
    const isDeviceReturn = task.taskCategory === 'DEVICE_RETURN';

    const devicesWithSettings = task?.devices ?? [];

    const aggregateDevicesFromTask = () => {
        const neededDevicesFromTask = task?.neededItems?.filter((value) => value.itemType === 'DEVICE');
        let aggregatedDevices: ItemRequest[] = [];
        neededDevicesFromTask.forEach((value) => {
            for (let i = 0; i < value.itemCount; i++) {
                aggregatedDevices.push(value);
            }
        });
        const oilInTask = task?.neededItems?.find((item) => item.itemType === 'OIL');
        return aggregatedDevices.map((value) => ({
            passportId: value.itemId,
            deviceName: '',
            model: '',
            oilName: '',
            oilType: '',
            oilId: oilInTask ? oilInTask.itemId : undefined,
            branchId: task.branchId,
            workHoursList: defaultDevice.workHoursList,
        }));
    };

    const devicesFromTask: BranchDevice[] =
        devicesWithSettings.length > 0
            ? devicesWithSettings.map((value) => {
                  return { ...value, branchId: task.branchId };
              })
            : aggregateDevicesFromTask();

    const { control, setValue, getValues, watch } = useForm<TaskDevices>({
        defaultValues: { devices: devicesFromTask, materials: materials },
    });

    const saveChanges = async (devices: BranchDevice[], materials?: MaterialBranch[]) => {
        const response = await updateTaskNeededItems(task, devices, materials);
        if (response) {
            setUsedItems([]);
            setTaskDevices(devices);
            await refetchTask(task.id);
            resourceCache(checkForItemsInInventory).clear();
            setHasChanges(false);
            resourceCache(useGetBranchPaymentInfo).clear();
        }
    };

    const addEmptyDevice = () => {
        const taskDevices = getValues('devices');
        taskDevices.push({ ...defaultDevice, branchId: task.branchId });
        setValue('devices', taskDevices);
        setHasChanges(true);
    };
    const removeTaskDevice = (index: number) => {
        setValue(
            'devices',
            getValues('devices').filter((value, i) => i !== index)
        );
        setHasChanges(true);
    };

    const hasMissingSelections = () => {
        const devices = getValues('devices');
        const hasInvalidNewDevice = (devicesToValidate: BranchDevice[]) =>
            devicesToValidate.some(
                ({ oilId, passportId, batteryId, shouldHasBattery }) =>
                    oilId == null || passportId == 0 || (passportId !== 0 && batteryId == null && shouldHasBattery)
            );

        if (isDeviceReturn) return devices.length === 0;
        return hasInvalidNewDevice(devices);
    };

    useEffect(() => {
        setTaskDevices(devicesFromTask);
    }, []);

    return (
        <div className='d-flex flex-column align-items-center w-100 p-3 pt-0 align-self-start new-request-device'>
            <div className='scrollable-container w-100'>
                <h5 className='w-100 text-center border-bottom pb-2'>
                    {getValues('devices').length > 0 ? t('devices') : t('noDevices')}
                </h5>

                <div className={`w-100 ${task.taskCategory === 'INSTALLATION' ? 'bg-new-devices' : ''}`}>
                    {watch('devices').map((device, index) => (
                        <Controller
                            key={index}
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <EditDevice
                                    {...{ task, value, onChange, control, index, setHasChanges }}
                                    removeDevice={() => removeTaskDevice(index)}
                                />
                            )}
                            name={`devices.${index}`}
                        />
                    ))}
                </div>

                <div className='w-100 d-flex justify-content-center'>
                    {task.taskCategory === 'INSTALLATION' && (
                        <div className='d-flex flex-column w-50'>
                            <CustomButton
                                className='w-100 mt-2'
                                title={t('device')}
                                variant='success'
                                icon={faPlus}
                                onClick={() => {
                                    addEmptyDevice();
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>

            {!isDeviceReturn && (
                <>
                    <h5 className='w-100 text-center border-bottom pb-2 pt-2'>{t('materials')}</h5>
                    <Controller
                        name={`materials`}
                        control={control}
                        render={({ field }) => (
                            <NewRequestMaterialsTable
                                onChange={(value) => {
                                    field.onChange(value);
                                    setMaterials(value);
                                    setHasChanges(true);
                                }}
                                materials={field.value ?? []}
                                assigneeId={task.assignee?.id}
                            />
                        )}
                    />
                </>
            )}

            <div className='d-flex w-100 justify-content-end mt-3'>
                <Button
                    disabled={hasMissingSelections()}
                    onClick={async () => {
                        if (hasChanges) {
                            await saveChanges(getValues('devices'), isDeviceReturn ? [] : materials);
                        }
                        nextStep!();
                    }}
                >
                    {t('next')}
                </Button>
            </div>
        </div>
    );
};

const EditDevice = ({
    value: device,
    onChange,
    task,
    removeDevice,
    index,
    control,
    setHasChanges,
}: {
    value: BranchDevice;
    onChange: (value: BranchDevice) => void;
    setHasChanges: (boolean: boolean) => void;
    task: Task;
    removeDevice: () => void;
    index: number;
    control: Control<TaskDevices>;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const [getAllDevicePassports] = useAsyncResource(useGetAllShortDevicePassports, false);
    const [getAllOilPassports] = useAsyncResource(useGetAllOilPassports, false);
    const [getAllBatteriesPassports] = useAsyncResource(useGetAllBatteriesPassports, false);
    const devicesPassports = getAllDevicePassports();
    const oilPassports = getAllOilPassports();
    const batteryPassports = getAllBatteriesPassports();
    const isDeviceReturn = task.taskCategory === 'DEVICE_RETURN';
    const isReturnedDevice = isDeviceReturn;
    const canConfigureDevice = !isReturnedDevice;

    const changeName = (name: string) => {
        onChange({ ...device, deviceName: name });
        setHasChanges(true);
    };

    const changeModel = (passport: ShortDevicePassport | null) => {
        if (passport?.id) {
            const requireBattery = Boolean(passport?.requireBattery);
            onChange({
                ...device,
                model: passport.model,
                passportId: passport.id,
                oilId: undefined,
                oilName: '',
                shouldHasBattery: requireBattery,
            });
        }
        setHasChanges(true);
    };

    const changeOil = (passport: OilPassport | null) => {
        if (passport?.id) onChange({ ...device, oilId: passport?.id, oilName: passport.name });
        setHasChanges(true);
    };

    const changeBattery = (passport: BatteryPassport | null) => {
        if (passport?.id) onChange({ ...device, batteryId: passport?.id, batteryName: passport.name });
        setHasChanges(true);
    };

    const changeWorkHours = (workHours: WorkHours[]) => {
        onChange({ ...device, workHoursList: workHours });
        setHasChanges(true);
    };

    useEffect(() => {
        if (!canConfigureDevice) return;
        const selected = devicesPassports.find((p) => p.id === device.passportId);
        const requireBattery = Boolean(selected?.requireBattery);
        onChange({
            ...device,
            shouldHasBattery: requireBattery,
        });
    }, [device.passportId, devicesPassports]);

    const requireBattery = !!devicesPassports.find((p) => p.id === device.passportId)?.requireBattery;

    return (
        <div className='w-100 border mt-1 d-flex'>
            <div className='border-end device-img d-flex justify-content-center align-items-center'>
                {device.passportId && device.passportId !== 0 ? (
                    <Suspense
                        fallback={
                            <div className='w-100 h-100 d-flex justify-content-center align-items-center'>
                                <Spinner animation={'border'} />
                            </div>
                        }
                    >
                        <PassportImages id={device.passportId} />
                    </Suspense>
                ) : (
                    <img className='passportImage noImg' src={noImage} alt={`passport img`} />
                )}
            </div>
            <div className='d-flex flex-column device-configuration'>
                {device.passportId == 0 ? <p className={`input-error `}> {t('deviceMissing')} </p> : <></>}
                {canConfigureDevice && device.passportId !== 0 && requireBattery && !device.batteryId && (
                    <p className='input-error'>{t('batteryMissing')}</p>
                )}
                {canConfigureDevice && device.oilId == undefined ? (
                    <p className={`input-error `}> {t('oilMissing')} </p>
                ) : (
                    <></>
                )}
                <div className='d-flex ps-1 py-1 align-items-center w-100'>
                    <div className='d-flex align-items-center w-100'>
                        <Form.Label className='mb-0 px-1 me-1'>{t('name')}</Form.Label>
                        <Form.Group className='d-flex align-items-center w-40' controlId='deviceName'>
                            <Controller
                                name={`devices.${index}.deviceName`}
                                {...{ control }}
                                render={({ field: { value, onChange } }) => (
                                    <Form.Control
                                        type='text'
                                        value={value ?? ''}
                                        onChange={(e) => {
                                            onChange(e.target.value);
                                            changeName(e.target.value);
                                        }}
                                    />
                                )}
                            />
                        </Form.Group>
                        <Form.Group className='d-flex align-items-center w-50' controlId='passportId'>
                            <Form.Label className='mb-0 px-1 me-1'>{t('Model')}</Form.Label>
                            <Select
                                onChange={changeModel}
                                isDisabled={task.taskCategory === 'MAINTENANCE' || isReturnedDevice}
                                placeholder={'Choose Device'}
                                styles={devicePassportsSelectStyles}
                                value={devicesPassports.find((passport) => passport.id === device?.passportId)}
                                options={devicesPassports}
                                maxMenuHeight={150}
                                getOptionLabel={({ name }) => name}
                                getOptionValue={({ passportId }) => passportId}
                            />
                        </Form.Group>
                        {canConfigureDevice && (
                            <Form.Group className='d-flex align-items-center w-50' controlId='oilId'>
                                <Form.Label className='mb-0 px-1 me-1'>{t('Oil')}</Form.Label>

                                <Select<OilPassport, false>
                                    onChange={(newValue) => {
                                        changeOil(newValue);
                                    }}
                                    placeholder={'Choose Oil'}
                                    value={oilPassports?.find((passport) => passport.id === device.oilId) || null}
                                    styles={oilPassportSelectStyles}
                                    options={oilPassports.filter(
                                        (passport) =>
                                            passport.forDeviceModel == null ||
                                            passport.forDeviceModel.includes(device?.model) ||
                                            passport.forDeviceModel.length === 0
                                    )}
                                    maxMenuHeight={150}
                                    getOptionLabel={({ name }) => name}
                                    getOptionValue={(option) => option.name}
                                />
                            </Form.Group>
                        )}

                        {canConfigureDevice &&
                            device.passportId != 0 &&
                            batteryPassports.filter((passport) => passport.model === device?.model).length > 0 && (
                                <Form.Group className='d-flex align-items-center w-50' controlId='batteryId'>
                                    <Form.Label className='mb-0 px-1 me-1'>{t('battery')}</Form.Label>

                                    <Select<BatteryPassport, false>
                                        onChange={(newValue) => {
                                            changeBattery(newValue);
                                        }}
                                        placeholder={'Choose Battery'}
                                        defaultValue={batteryPassports?.find(
                                            (passport) => passport.id === device.batteryId
                                        )}
                                        styles={BatteryPassportSelectStyles}
                                        options={batteryPassports.filter(
                                            (passport) => passport.model === device?.model
                                        )}
                                        maxMenuHeight={150}
                                        getOptionLabel={({ name }) => name}
                                        getOptionValue={(option) => option.name}
                                    />
                                </Form.Group>
                            )}
                    </div>

                    {(task.taskCategory === 'INSTALLATION' || isDeviceReturn) && (
                        <div className='d-flex w-20 mx-1  justify-content-end'>
                            <CustomButton
                                icon={faTrash}
                                iconClass='fa-fw'
                                onClick={removeDevice}
                                className='color-main p-1 py-0'
                            />
                        </div>
                    )}
                </div>
                {canConfigureDevice && (
                    <WorkHoursInner
                        onChange={changeWorkHours}
                        hasDevice={true}
                        list={device.workHoursList}
                        isDisabled={false}
                    />
                )}
            </div>
        </div>
    );
};
